import 'altcha';
import { register } from './auth-api';
import { API_BASE } from '../network/api';
import { t } from '../../engine/i18n';

const devUsername = import.meta.env.DEV ? 'admin' : '';
const devEmail = import.meta.env.DEV ? 'admin@tactus.dev' : '';
const devPassword = import.meta.env.DEV ? 'tactus123' : '';

const INPUT_CLS =
  'w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow';
const TOGGLE_CLS =
  'absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-text-dim hover:text-gold bg-transparent border-0 cursor-pointer';

export function renderRegister(root: HTMLElement, onSuccess: () => void): () => void {
  root.innerHTML = `
    <style>
      @keyframes reg-shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-5px); } 40%,80% { transform: translateX(5px); } }
      .reg-shake { animation: reg-shake 320ms ease; }
      @media (prefers-reduced-motion: reduce) { .reg-shake { animation: none; } }
    </style>
    <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient">
      <form class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift" id="register-form" novalidate>
        <h1 class="mb-2 font-heading text-2xl text-gold tracking-wide">${t('auth.register.heading')}</h1>
        <p class="mb-6 text-sm text-text-dim">${t('auth.register.sub')}</p>
        <div id="register-error" class="mb-3.5 px-3 py-2.5 bg-error/[0.08] border border-error/40 rounded text-error text-[13px]" hidden></div>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.username')}</span>
          <input type="text" name="username" minlength="3" maxlength="32" pattern="[a-zA-Z0-9_-]+" required value="${devUsername}"
            class="${INPUT_CLS}" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.email')}</span>
          <input type="email" name="email" autocomplete="email" required value="${devEmail}"
            class="${INPUT_CLS}" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.phone')} <span class="opacity-60">${t('auth.register.phoneOptional')}</span></span>
          <input type="tel" name="phone" autocomplete="tel" placeholder="${t('auth.register.phonePlaceholder')}"
            class="${INPUT_CLS}" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.password')}</span>
          <span class="relative block">
            <input type="password" name="password" autocomplete="new-password" minlength="8" required value="${devPassword}"
              class="${INPUT_CLS} pr-14" />
            <button type="button" class="${TOGGLE_CLS}" data-toggle="password" aria-label="${t('auth.register.show')}">${t('auth.register.show')}</button>
          </span>
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">${t('auth.register.confirmPassword')}</span>
          <span class="relative block">
            <input type="password" name="confirm" autocomplete="new-password" minlength="8" required value="${devPassword}"
              class="${INPUT_CLS} pr-14" />
            <button type="button" class="${TOGGLE_CLS}" data-toggle="confirm" aria-label="${t('auth.register.show')}">${t('auth.register.show')}</button>
          </span>
        </label>
        <label class="flex items-start gap-2.5 mb-3.5 cursor-pointer select-none" id="register-terms-row">
          <input type="checkbox" name="terms" class="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-gold)]" />
          <span class="text-[12px] leading-snug text-text-dim">
            ${t('auth.register.termsAgree')} <a href="/legal/terms-of-service" target="_blank" rel="noopener">${t('auth.register.terms')}</a>
            ${t('auth.register.and')} <a href="/legal/privacy-policy" target="_blank" rel="noopener">${t('auth.register.privacy')}</a>
          </span>
        </label>
        <div class="mb-3.5">
          <altcha-widget challenge="${API_BASE}/auth/altcha-challenge" auto="onload" name="altcha"></altcha-widget>
        </div>
        <button type="submit" class="inline-block w-full p-3 bg-gold text-bg font-bold border-0 rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed" id="register-btn">${t('auth.register.submit')}</button>
        <p class="mt-5 text-[13px] text-text-dim text-center">
          ${t('auth.register.haveAccount')} <a href="/login">${t('auth.register.signIn')}</a>
        </p>
      </form>
    </div>
  `;

  const form = root.querySelector('#register-form') as HTMLFormElement;
  const errEl = root.querySelector('#register-error') as HTMLDivElement;
  const btn = root.querySelector('#register-btn') as HTMLButtonElement;
  const termsRow = root.querySelector('#register-terms-row') as HTMLLabelElement;

  // Show/hide toggles on both password fields.
  const onToggle = (e: Event) => {
    const el = e.target as HTMLElement;
    if (!el.dataset?.toggle) return;
    const input = form.querySelector(`input[name="${el.dataset.toggle}"]`) as HTMLInputElement | null;
    if (!input) return;
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    el.textContent = show ? t('auth.register.hide') : t('auth.register.show');
    el.setAttribute('aria-label', show ? t('auth.register.hide') : t('auth.register.show'));
  };
  form.addEventListener('click', onToggle);

  const fail = (msg: string) => {
    errEl.textContent = msg;
    errEl.hidden = false;
  };

  const handler = async (e: Event) => {
    e.preventDefault();
    errEl.hidden = true;
    const fd = new FormData(form);
    const username = String(fd.get('username') ?? '').trim();
    const password = String(fd.get('password') ?? '');
    const confirm = String(fd.get('confirm') ?? '');
    const phone = String(fd.get('phone') ?? '').trim();

    if (!username) return fail(t('auth.register.errUsername'));
    if (password.length < 8) return fail(t('auth.register.errPasswordLength'));
    if (password !== confirm) return fail(t('auth.register.errPasswordMatch'));
    if (phone && !/^[+\d][\d\s\-().]{4,28}$/.test(phone)) return fail(t('auth.register.errPhone'));
    if (!fd.get('terms')) {
      termsRow.classList.remove('reg-shake');
      void termsRow.offsetWidth; // restart the animation
      termsRow.classList.add('reg-shake');
      return fail(t('auth.register.errTerms'));
    }
    const altchaPayload = fd.get('altcha') as string | null;
    if (!altchaPayload) return fail(t('auth.register.errVerify'));

    btn.disabled = true;
    btn.textContent = t('auth.register.submitting');
    try {
      await register(
        String(fd.get('email') ?? ''),
        password,
        altchaPayload,
        username,
        phone || undefined,
        true,
      );
      onSuccess();
    } catch (err) {
      fail((err as Error).message);
      btn.disabled = false;
      btn.textContent = t('auth.register.submit');
    }
  };
  form.addEventListener('submit', handler);
  return () => {
    form.removeEventListener('submit', handler);
    form.removeEventListener('click', onToggle);
  };
}
